import React, { useEffect, useState, useRef } from "react";
import ReactCrop, { Crop } from "react-image-crop";
import { Modal, ModalFooter, Row } from "react-bootstrap";
import "react-image-crop/dist/ReactCrop.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowUp } from "@fortawesome/free-solid-svg-icons";
import { Button, Col, Input, Label } from "reactstrap";
import "./index.css";
import CancelButtonComponents from "../CancelButtonComponent";
import ButtonComponentV2 from "../ButtonComponentV2";
import { toast } from "react-toastify";

interface ImageUploadProps {
    imageUrl: string;
    onUpload: (data: {
        imgSrc: string;
        formData: File | null;
        fileExtension: string | undefined;
    }) => void;
    imgSizeLogic?: boolean;
    imgRemoveLogic?: boolean;
}

const IDIMGUploadComponent: React.FC<ImageUploadProps> = ({
    onUpload,
    imgSizeLogic,
}) => {
    const [selectedImage, setSelectedImage] = useState<{
        file: File | null;
        name: string | null;
    }>({ file: null, name: null });

    const [croppedImage, setCroppedImage] = useState<{
        file: File | null;
        name: string | null;
    }>({ file: null, name: null });

    const [crop, setCrop] = useState<Crop>({
        unit: "%",
        x: 0,
        y: 0,
        width: 100,
        height: 100,
    });

    const [show, setShow] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isImageSizeBetter, setIsImageSizeBetter] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [error, setError] = useState<string | null>(null);
    const imgRef = useRef<HTMLImageElement | null>(null);

    const handleToggle = () => {
        setShow(!show);
    };

    const handleClose = () => {
        handleToggle();
    };

    useEffect(() => {
        if (selectedImage?.file !== null) {
            handleToggle();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedImage]);

    useEffect(() => {
        if (
            crop?.width > 300 &&
            crop?.width < 350 &&
            crop?.height > 200 &&
            crop?.height < 250
        ) {
            setIsImageSizeBetter(true);
        } else {
            setIsImageSizeBetter(false);
        }
    }, [crop]);

    const handleSetCroppedImage = () => {
        if (!imgRef.current || !crop.width || !crop.height) {
            return;
        }

        if (crop.width === 100 && crop.height === 100) {
            setCroppedImage(selectedImage);
            onUpload({
                imgSrc: URL.createObjectURL(selectedImage.file!),
                formData: selectedImage.file,
                fileExtension: selectedImage.name?.split(".").pop(),
            });
            handleToggle();
            return;
        }

        const canvas = document.createElement("canvas");
        const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
        const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
        canvas.width = crop.width * scaleX;
        canvas.height = crop.height * scaleY;
        const ctx = canvas.getContext("2d");

        if (ctx) {
            ctx.drawImage(
                imgRef.current,
                crop.x * scaleX,
                crop.y * scaleY,
                crop.width * scaleX,
                crop.height * scaleY,
                0,
                0,
                crop.width * scaleX,
                crop.height * scaleY
            );

            canvas.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], selectedImage.name ?? "cropped.jpg", {
                        type: blob.type,
                    });
                    const fileUrl = URL.createObjectURL(file);
                    setCroppedImage({ file, name: selectedImage.name });
                    onUpload({
                        imgSrc: fileUrl,
                        formData: file,
                        fileExtension: selectedImage.name?.split(".").pop(),
                    });
                    handleToggle();
                }
            }, "image/jpeg");
        }
    };

    const handleImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const fileExtension = file.name.split(".").pop()?.toLowerCase();

            // Allow only PNG, JPG, and JPEG files
            if (fileExtension !== "png" && fileExtension !== "jpg" && fileExtension !== "jpeg") {
                toast.warning('Only PNG, JPG, and JPEG files are allowed.')
                setError("Only PNG, JPG, and JPEG files are allowed.");
                return;
            }

            if (!imgSizeLogic) {
                if (file.size > 10 * 1024 * 1024) {
                    setError("File size must be below 10MB.");
                    return;
                }
            }

            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    if (imgSizeLogic) {
                        setSelectedImage({ file, name: file.name });
                        onUpload({
                            imgSrc: URL.createObjectURL(file),
                            formData: file,
                            fileExtension,
                        });
                    } else {
                        setError(null);
                        setSelectedImage({ file, name: file.name });
                        onUpload({
                            imgSrc: URL.createObjectURL(file),
                            formData: file,
                            fileExtension,
                        });
                    }

                    // Set the initial crop to match the image dimensions
                    setCrop({
                        unit: "%",
                        x: 0,
                        y: 0,
                        width: 60,
                        height: 40,
                    });
                };
                img.src = event.target?.result as string;
            };
            reader.readAsDataURL(file);
        }
    };


    const handleRemoveImage = () => {
        setSelectedImage({ file: null, name: null });
        setCroppedImage({ file: null, name: null });
        onUpload({ imgSrc: "", formData: null, fileExtension: "" });
        setError(null);
    };

    return (
        <>
            <Row>
                <Col lg="4">
                    <Label>ID Document</Label>
                </Col>
                <Col lg="8">
                    <div className="imageInputField">
                        {croppedImage.file ? (
                            <div className="imgDiv">
                                <img
                                    src={URL.createObjectURL(croppedImage.file)}
                                    className="img"
                                    alt="Cropped"
                                />
                                {!imgSizeLogic ? (
                                    <p className="pStyle">{croppedImage.name}</p>
                                ) : (
                                    <p></p>
                                )}
                                <Button
                                    color="danger"
                                    size="sm"
                                    onClick={handleRemoveImage}
                                    className="remove-btn-style"
                                >
                                    Remove Picture
                                </Button>
                            </div>
                        ) : (
                            <div className="imageUploader">
                                {!imgSizeLogic && (
                                    <>
                                        <Label htmlFor="fileInput" className="labelInsideInput">
                                            <FontAwesomeIcon
                                                icon={faCloudArrowUp}
                                                className="cloudArrow"
                                            />
                                            <span className="spanColor">Click to upload</span>
                                            PNG, JPG, or JPEG (up to 10MB), 320-1,144 pixels wide
                                        </Label>
                                        <Input
                                            type="file"
                                            accept="image/*"
                                            className="hiddenInput"
                                            id="fileInput"
                                            onChange={handleImage}
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            <Modal show={show} onHide={handleClose} backdrop="static">
                <Modal.Header closeButton>Crop Image</Modal.Header>
                <Modal.Body>
                    <div className="imageCropContainer">
                        {selectedImage.file && (
                            <ReactCrop
                                crop={crop}
                                onChange={(newCrop: Crop) => setCrop(newCrop)}
                                onComplete={(c: Crop) => setCrop(c)}
                                aspect={16 / 9}
                            >
                                <img
                                    ref={imgRef}
                                    src={URL.createObjectURL(selectedImage.file)}
                                    alt="Crop"
                                />
                            </ReactCrop>
                        )}
                    </div>
                </Modal.Body>
                <ModalFooter>
                    <CancelButtonComponents onClick={handleClose} titel={"Cancel"} />
                    <ButtonComponentV2
                        onClick={handleSetCroppedImage}
                        titel={"Upload Image"}
                        color="#fff"
                        backgroundColor="#590B21"
                    />
                </ModalFooter>
            </Modal>
        </>
    );
};

export default IDIMGUploadComponent;
